import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useResizeObserverCallback } from '@databases/react';
import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const GRADIENT_HEIGHT = 35;

const gradientBaseStyle = xcss({
  position: 'sticky',
  left: 'space.0',
  right: 'space.0',
  height: `${GRADIENT_HEIGHT}px`,
  pointerEvents: 'none',
  zIndex: '1',
  transition: 'opacity 0.2ms ease-in-out',
  // Negative margin so gradients don't take up layout space
  flexShrink: 0,
});

/**
 * Renders 35px gradient fades at the top and/or bottom edge of a scrollable
 * menu to indicate more content is available.
 *
 * The gradients use `position: sticky` so they stay fixed at the edges of
 * the scroll viewport while the content scrolls beneath them. Negative margins
 * prevent them from affecting layout.
 *
 * This component finds its nearest scrollable ancestor (e.g. the Popup wrapper
 * that has `overflow-y: auto`) and observes its scroll position.
 */
const ScrollGradientWrapper = ({ children }: { children: ReactNode }) => {
  const topRef = useRef<HTMLDivElement>(null);
  const [showTopGradient, setShowTopGradient] = useState(false);
  const [showBottomGradient, setShowBottomGradient] = useState(false);

  const updateGradients = useCallback((el: Element) => {
    const { scrollTop, scrollHeight, clientHeight } = el;
    const isScrollable = scrollHeight > clientHeight;

    if (!isScrollable) {
      setShowTopGradient(false);
      setShowBottomGradient(false);
      return;
    }

    setShowTopGradient(scrollTop > 1);
    setShowBottomGradient(scrollTop + clientHeight < scrollHeight - 1);
  }, []);

  // Find the nearest scrollable ancestor scoped to the popup boundary.
  // When shouldRenderToParent is used, the popup renders inline inside
  // the page's own scroll container.
  // We stop traversal at the popup's root element (identified by `data-placement`,
  // which @atlaskit/popup's Popper wrapper sets) so we don't pick up an
  // unrelated outer scroll container.
  const findScrollableAncestor = useCallback((node: Element): Element | null => {
    let current = node.parentElement;
    while (current) {
      const style = window.getComputedStyle(current);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        return current;
      }

      if (current.hasAttribute('data-placement')) {
        return null;
      }
      current = current.parentElement;
    }
    return null;
  }, []);

  const [scrollableEl, setScrollableEl] = useState<HTMLElement | null>(null);

  // Discover the scrollable ancestor once the top ref is mounted.
  useEffect(() => {
    const el = topRef.current;
    if (!el) {
      return;
    }
    setScrollableEl(findScrollableAncestor(el) as HTMLElement | null);
  }, [findScrollableAncestor]);

  // Elements to observe for resizes: always include the wrapper element,
  // and include the scrollable ancestor once discovered.
  const observedEls = useMemo(() => {
    const els: HTMLElement[] = [];
    if (topRef.current) {
      els.push(topRef.current);
    }
    if (scrollableEl) {
      els.push(scrollableEl);
    }
    return els;
  }, [scrollableEl]);

  // Use the shared ResizeObserver hook instead of creating one per instance.
  useResizeObserverCallback(
    observedEls,
    useCallback(() => {
      const el = topRef.current;
      if (!el) {
        return;
      }

      // Re-discover the scrollable ancestor if not yet found (e.g. the popup
      // may become scrollable after a resize).
      let target = scrollableEl;
      if (!target) {
        target = findScrollableAncestor(el) as HTMLElement | null;
        if (target) {
          setScrollableEl(target);
        }
      }
      if (target) {
        updateGradients(target);
      }
    }, [scrollableEl, findScrollableAncestor, updateGradients])
  );

  // Attach a scroll listener to the scrollable ancestor.
  useEffect(() => {
    if (!scrollableEl) {
      return;
    }

    const handleScroll = () => {
      updateGradients(scrollableEl);
    };

    scrollableEl.addEventListener('scroll', handleScroll, { passive: true });
    updateGradients(scrollableEl);

    return () => {
      scrollableEl.removeEventListener('scroll', handleScroll);
    };
  }, [scrollableEl, updateGradients]);

  return (
    <>
      {/* Top gradient — sticky to the top of the scroll viewport */}
      <Box
        ref={topRef}
        testId="scroll-gradient-top"
        xcss={[
          gradientBaseStyle,
          xcss({
            top: '0',
            background: `linear-gradient(to bottom, ${token(
              'elevation.surface.overlay',
              '#FFFFFF'
            )}, transparent)`,
            opacity: showTopGradient ? 1 : 0,
          }),
        ]}
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
        style={{ marginBottom: `-${GRADIENT_HEIGHT}px` }}
      />

      {children}

      {/* Bottom gradient — sticky to the bottom of the scroll viewport */}
      <Box
        testId="scroll-gradient-bottom"
        xcss={[
          gradientBaseStyle,
          xcss({
            bottom: '0',
            background: `linear-gradient(to top, ${token(
              'elevation.surface.overlay',
              '#FFFFFF'
            )}, transparent)`,
            opacity: showBottomGradient ? 1 : 0,
          }),
        ]}
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
        style={{ marginTop: `-${GRADIENT_HEIGHT}px` }}
      />
    </>
  );
};

export default ScrollGradientWrapper;
